import {
  LocationQueryEnum,
  type LocationResponse,
  type RegionType,
} from "@b2bportal/air-shopping-api";
import {
  fetchChfarFlights,
  type FetchIataCodeLookupResponse,
  fetchIataLocation,
  fetchLocationAutocomplete,
  logger,
  trackEvent,
} from "@hopper-b2b/api";
import {
  AIR_ENTRY,
  type AirEntryProperties,
  FlightShopType,
  type LocationQuery,
  type OfferWithSuggested,
  SliceStopCountFilter,
  TripCategory,
} from "@hopper-b2b/types";
import dayjs from "dayjs";
import { call, put, putResolve, select } from "redux-saga/effects";
import { fetchTripSummaries as fetchFlights } from "../../../api/v3/shop/fetchTripSummaries";
import Logger from "../../../helpers/Logger";
import {
  flightApiConfigStoreKey,
  type IStoreState,
} from "../../../reducers/types";
import { PATH_HOME } from "../../../utils/urlPaths";
import { actions as searchActions } from "../../search/actions";
import { setMobileSearchProgress } from "../../search/actions/actions";
import { MobileFlightSearchStep } from "../../search/reducer";
import { normalizePassengerCounts } from "../../search/utils/passengerCounts";
import { actions } from "../actions";
import type { IShopParams } from "../actions/actions";
import {
  getAirEntryProperties,
  getPriceFreezeOfferWithSuggested,
  maxFlightPriceSelectorV2,
} from "../reducer/selectors";
import { buildFlightsRequestParams } from "../utils/flightsRequestParams";
import type { IFlightShopParsedQuery } from "../utils/parseQueryString";
import {
  buildFlightShopQueryDiagnostics,
  buildPricePredictionDiagnostics,
} from "../utils/priceWatchDiagnostics";
import {
  normalizeFlightRegionType,
  resolveFlightLocation,
} from "../utils/resolveFlightLocation";
import {
  getExistingStateVariables,
  populateFlightShopQueryParametersFromState,
} from "./populateShopQueryParamsSaga";

const shouldRedirect = ({
  origin,
  destination,
  departureDate,
  tripCategory,
  returnDate,
  stopsOption,
}: any) =>
  !origin ||
  !destination ||
  !departureDate ||
  !stopsOption ||
  (tripCategory === TripCategory.ROUND_TRIP && !returnDate);

export function* fetchTripSummariesV3(
  action: actions.IFetchTripSummariesV3
): Generator<any, any, any> {
  try {
    const shopFromParams = yield call(setUpFlightShopParams, action);
    const {
      departureDate,
      returnDate,
      origin,
      originType,
      destination,
      destinationType,
      adultsCount,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
      noLCC,
      stopsOption,
      priceWatchEntrySource,
    } = shopFromParams;

    const state: IStoreState = yield select();
    const apiConfig = state[flightApiConfigStoreKey];
    const requestBody: IShopParams = {
      origin: { ...origin.id.code },
      destination: { ...destination.id.code },
      ...(returnDate && {
        returnDate: dayjs(returnDate).format("YYYY-MM-DD"),
      }),
      departureDate: dayjs(departureDate).format("YYYY-MM-DD"),
      adultsCount,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
      stopsOption: stopsOption ? stopsOption : SliceStopCountFilter.ANY_NUMBER,
      fareclassOptionFilter: {
        basic: false,
        standard: noLCC,
        enhanced: noLCC,
        premium: noLCC,
        luxury: noLCC,
      },
      originType: originType ? originType : "airport",
      destinationType: destinationType ? destinationType : "airport",
    };

    const chfarId = state.flightShop.chfarId;
    const isChfarFlightShop =
      state.flightShop.flightShopType === FlightShopType.CHFAR_EXERCISE &&
      chfarId;
    const response = isChfarFlightShop
      ? yield fetchChfarFlights(
          requestBody,
          chfarId,
          action.isMobile,
          apiConfig
        )
      : yield fetchFlights(requestBody, action.isMobile, apiConfig);

    const properties: AirEntryProperties | null = yield select(
      getAirEntryProperties
    );
    yield trackEvent(
      {
        eventName: AIR_ENTRY,
        properties,
      },
      apiConfig
    );

    if (response.Response === "Failure") {
      yield put(actions.setTripSummariesError());
      yield put(actions.setPredictionError());
    } else {
      // note: the order does matter here! having redux set priceFreezeOffer first will allow it to update priceFreezeOffer
      // related selectors prior to that of the prediction
      yield putResolve(
        actions.setFlights(
          response.value.flights,
          buildFlightsRequestParams({
            origin,
            destination,
            departureDate,
            returnDate,
            adultsCount,
            childrenCount,
            infantsInSeatCount,
            infantsOnLapCount,
          })
        )
      );
      yield putResolve(
        actions.setPriceFreezeOffer(response.value.priceFreezeOffer)
      );
      yield putResolve(actions.setPrediction(response.value.prediction));

      const priceFreeze: OfferWithSuggested | null = yield select(
        getPriceFreezeOfferWithSuggested
      );
      if (priceFreeze) {
        yield put(actions.fetchTripDetails(priceFreeze.cheapestTrip.tripId));
      }
      const maxPriceFlight: number = yield select(maxFlightPriceSelectorV2);
      if (priceWatchEntrySource && priceWatchEntrySource !== "organic") {
        logger()?.info("[FlightShopPrediction] Live shop prediction", {
          event: "flight_shop_price_prediction_observed",
          shopApi: "v3",
          entrySource: priceWatchEntrySource,
          ...buildPricePredictionDiagnostics(response.value.prediction),
          maxFlightPriceFilter: maxPriceFlight,
        });
      }
      yield put(searchActions.setMaxPriceFilter(maxPriceFlight));

      yield put(searchActions.setAwaitingRefetch(false));
    }
  } catch (e) {
    yield put(actions.setTripSummariesError());
    yield put(actions.setPredictionError());
    Logger.debug(e);
  }
}

function* setUpFlightShopParams({
  history,
  isIataEnabled,
}: actions.IFetchTripSummariesV3) {
  const state: IStoreState = yield select();
  const existingState = getExistingStateVariables(state);

  let {
    departureDate,
    returnDate,
    origin,
    destination,
    tripCategory,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
    stopsOption,
    noLCC,
    destinationType,
    originType,
  } = existingState;

  const parsedQueryString: IFlightShopParsedQuery =
    yield populateFlightShopQueryParametersFromState({
      state,
      history,
      isV2: true,
    });

  const parsedQueryDiagnostics =
    buildFlightShopQueryDiagnostics(parsedQueryString);
  if (parsedQueryDiagnostics.entrySource !== "organic") {
    logger()?.info("[FlightShopHydration] Shop query entry", {
      event: "flight_shop_price_watch_entry",
      shopApi: "v3",
      ...parsedQueryDiagnostics,
    });
  }

  if (
    !origin ||
    !destination ||
    !departureDate ||
    (tripCategory === TripCategory.ROUND_TRIP && !returnDate)
  ) {
    logger()?.info("[FlightShopHydration] Starting parameter hydration", {
      event: "flight_shop_parameter_hydration_started",
      shopApi: "v3",
      hydrationBranch: "missing_state",
      ...parsedQueryDiagnostics,
    });
    const { correspondingDestination, correspondingOrigin } =
      yield fetchOriginDestination(parsedQueryString, isIataEnabled);

    [
      origin,
      destination,
      departureDate,
      returnDate,
      originType,
      destinationType,
      tripCategory,
      adultsCount,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
    ] = [
      correspondingOrigin,
      correspondingDestination,
      parsedQueryString.departureDate,
      parsedQueryString.returnDate,
      parsedQueryString.originType as RegionType,
      parsedQueryString.destinationType as RegionType,
      parsedQueryString.tripCategory,
      parsedQueryString.adultsCount,
      parsedQueryString.childrenCount,
      parsedQueryString.infantsInSeatCount,
      parsedQueryString.infantsOnLapCount,
    ];
    // Use Defaults
    stopsOption =
      (parsedQueryString.stopsOption as SliceStopCountFilter) ||
      SliceStopCountFilter.ANY_NUMBER;
    noLCC = parsedQueryString.noLCC || false;
    tripCategory = parsedQueryString.tripCategory || TripCategory.ROUND_TRIP;

    // If we are missing the data we need from both the state and the query params
    // we should redirect the user home.
    if (
      shouldRedirect({
        departureDate,
        origin,
        destination,
        returnDate,
        tripCategory,
        stopsOption,
      })
    ) {
      logger()?.warn("[FlightShopHydration] Parameter hydration failed", {
        event: "flight_shop_parameter_hydration_failed",
        shopApi: "v3",
        hydrationBranch: "missing_state",
        ...parsedQueryDiagnostics,
        resolvedOriginCode: correspondingOrigin?.id?.code?.code ?? null,
        resolvedDestinationCode:
          correspondingDestination?.id?.code?.code ?? null,
      });
      yield putResolve(
        setMobileSearchProgress(MobileFlightSearchStep.LocationSearch)
      );
      history.push(PATH_HOME);
      return;
    }
    // Order here matters because of the reducer structure.
    // Set trip category resets the returnDate field.
    yield putResolve(searchActions.setTripCategory(tripCategory));
    yield putResolve(searchActions.setOrigin(origin));
    yield putResolve(searchActions.setOriginType(originType));
    yield putResolve(searchActions.setDestination(destination));
    yield putResolve(searchActions.setDestinationType(destinationType));
    yield putResolve(searchActions.setDepartureDate(departureDate));
    yield putResolve(searchActions.setReturnDate(returnDate));
    yield putResolve(searchActions.setStopsOption(stopsOption));

    yield putResolve(
      searchActions.setPassengerCounts(
        normalizePassengerCounts({
          adultsCount,
          childrenCount,
          infantsInSeatCount,
          infantsOnLapCount,
        })
      )
    );
    yield putResolve(
      searchActions.setFareclassOptionFilter({
        basic: false,
        standard: noLCC,
        enhanced: noLCC,
        premium: noLCC,
        luxury: noLCC,
      })
    );
  } else if (
    // note: when it's from FlightWatch, it should treat parsedQueryString as the source of truth.
    parsedQueryString.isFromFlightWatch
  ) {
    logger()?.info("[FlightShopHydration] Starting parameter hydration", {
      event: "flight_shop_parameter_hydration_started",
      shopApi: "v3",
      hydrationBranch: "watch_flag",
      ...parsedQueryDiagnostics,
    });
    const { correspondingDestination, correspondingOrigin } =
      yield fetchOriginDestination(parsedQueryString, isIataEnabled);
    [
      origin,
      destination,
      departureDate,
      returnDate,
      originType,
      destinationType,
      tripCategory,
      adultsCount,
      childrenCount,
      infantsInSeatCount,
      infantsOnLapCount,
      stopsOption,
      noLCC,
    ] = [
      correspondingOrigin,
      correspondingDestination,
      parsedQueryString.departureDate,
      parsedQueryString.returnDate,
      parsedQueryString.originType as RegionType,
      parsedQueryString.destinationType as RegionType,
      parsedQueryString.tripCategory,
      parsedQueryString.adultsCount,
      parsedQueryString.childrenCount || 0,
      parsedQueryString.infantsInSeatCount,
      parsedQueryString.infantsOnLapCount,
      parsedQueryString.stopsOption as SliceStopCountFilter,
      parsedQueryString.noLCC,
    ];

    // If we are missing the data we need from both the state and the query params
    // we should redirect the user home.
    if (
      shouldRedirect({
        departureDate,
        origin,
        destination,
        returnDate,
        tripCategory,
        stopsOption,
      })
    ) {
      yield putResolve(
        setMobileSearchProgress(MobileFlightSearchStep.LocationSearch)
      );
      history.push(PATH_HOME);
      return;
    }
    // Order here matters because of the reducer structure.
    // Set trip category resets the returnDate field.
    yield putResolve(searchActions.setTripCategory(tripCategory));
    yield putResolve(searchActions.setOrigin(origin));
    yield putResolve(searchActions.setOriginType(originType));
    yield putResolve(searchActions.setDestination(destination));
    yield putResolve(searchActions.setDestinationType(destinationType));
    yield putResolve(searchActions.setDepartureDate(departureDate));
    yield putResolve(searchActions.setReturnDate(returnDate));
    yield putResolve(searchActions.setStopsOption(stopsOption));
    yield putResolve(
      searchActions.setPassengerCounts(
        normalizePassengerCounts({
          adultsCount,
          childrenCount,
          infantsInSeatCount,
          infantsOnLapCount,
        })
      )
    );
    yield putResolve(
      searchActions.setFareclassOptionFilter({
        basic: false,
        standard: noLCC,
        enhanced: noLCC,
        premium: noLCC,
        luxury: noLCC,
      })
    );
  }

  const normalizedPassengerCounts = normalizePassengerCounts({
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
  });

  return {
    departureDate,
    returnDate,
    origin,
    destination,
    ...normalizedPassengerCounts,
    stopsOption,
    noLCC,
    priceWatchEntrySource: parsedQueryDiagnostics.entrySource,
  };
}

function* fetchOriginDestination(
  parsedQueryString: IFlightShopParsedQuery,
  isIataEnabled?: boolean
) {
  const correspondingOrigin = yield fetchLocation(
    parsedQueryString.origin,
    Boolean(isIataEnabled),
    parsedQueryString.originType
  );
  const correspondingDestination = yield fetchLocation(
    parsedQueryString.destination,
    Boolean(isIataEnabled),
    parsedQueryString.destinationType
  );
  return { correspondingDestination, correspondingOrigin };
}

function* fetchLocation(
  locationQuery: string,
  isIataCodeLookup: boolean,
  regionType?: string
) {
  if (isIataCodeLookup) {
    const locationResult: FetchIataCodeLookupResponse = yield fetchIataLocation(
      {
        iataCode: locationQuery,
      }
    );
    const normalizedRegionType = normalizeFlightRegionType(regionType);

    if (regionType && !normalizedRegionType) {
      return undefined;
    }

    return normalizedRegionType
      ? locationResult[normalizedRegionType]
      : locationResult.airport || locationResult.city;
  } else {
    const requestBody: LocationQuery = {
      l: locationQuery,
      LocationQuery: LocationQueryEnum.Label,
    };
    const autocompleteResults: LocationResponse =
      yield fetchLocationAutocomplete(requestBody);
    return resolveFlightLocation(
      autocompleteResults,
      locationQuery,
      regionType
    );
  }
}
